import type {
  ComponentProps,
  ComponentType,
  PropsWithChildren,
  ReactNode,
} from "react";
import { useCallback } from "react";
import { ArrowDownIcon } from "lucide-react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

import { Button } from "../../ui/button";
import { cn } from "../../ui/utils";

type StickToBottomComponentProps = Parameters<typeof StickToBottom>[0];
type StickToBottomContentProps = Parameters<
  (typeof StickToBottom)["Content"]
>[0];

const StickToBottomRoot =
  StickToBottom as unknown as ComponentType<StickToBottomComponentProps>;
const StickToBottomContent = (
  StickToBottom as unknown as {
    Content: ComponentType<StickToBottomContentProps>;
  }
).Content;

export type ConversationProps = PropsWithChildren<
  StickToBottomComponentProps & { className?: string }
>;

export const Conversation = ({
  className,
  children,
  ...props
}: ConversationProps) => (
  <StickToBottomRoot
    className={cn("relative flex-1 overflow-y-auto no-scrollbar", className)}
    initial="smooth"
    resize="smooth"
    role="log"
    {...props}
  >
    {children}
  </StickToBottomRoot>
);

export type ConversationContentProps = PropsWithChildren<
  StickToBottomContentProps & { className?: string }
>;

export const ConversationContent = ({
  className,
  children,
  ...props
}: ConversationContentProps) => (
  <StickToBottomContent
    className={cn("p-4 no-scrollbar", className)}
    {...props}
  >
    {children}
  </StickToBottomContent>
);

export type ConversationEmptyStateProps = Omit<
  ComponentProps<"div">,
  "title"
> & {
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
};

export const ConversationEmptyState = ({
  className,
  title = "No messages yet",
  description = "Start a conversation to see messages here",
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) => (
  <div
    className={cn(
      "flex size-full flex-col items-center justify-center gap-3 p-8 text-center",
      className,
    )}
    {...props}
  >
    {children ?? (
      <>
        {icon && <div className="text-muted-foreground">{icon}</div>}
        <div className="space-y-1">
          <h3 className="text-sm font-medium">{title}</h3>
          {description && (
            <p className="text-muted-foreground text-sm">{description}</p>
          )}
        </div>
      </>
    )}
  </div>
);

export type ConversationScrollButtonProps = ComponentProps<typeof Button>;

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  if (isAtBottom) return null;

  return (
    <Button
      className={cn(
        "absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full shadow-md bg-white",
        className,
      )}
      onClick={handleScrollToBottom}
      size="icon"
      type="button"
      variant="widget"
      {...props}
    >
      <ArrowDownIcon className="size-4" />
    </Button>
  );
};
